import _ from 'lodash';

import { getLinkDifferences, getLinkShape, getListingState } from './links_to_check_helpers';

// At most one tip per card, most specific first. Likely wrong cards carry their number in the reason line instead.
export const getLinkHint = link => {
  if (['alert', 'same_item'].includes(link.cardType)) return null;
  const tip = getActionChangingTip(link) || getSiteTip(link) || getSituationTip(link) || SITUATION_TIPS[TAB_DEFAULT_TIPS[link.question]];
  return (typeof tip === 'function' ? tip(link) : tip) || null;
};

// Shown on the New Product form's brand field when the brand has two catalog records.
export const getBrandTip = AllBrand_id => {
  const pair = BRAND_TIPS.find(({ use, other }) => [use.id, other.id].includes(Number(AllBrand_id)));
  if (!pair) return null;
  const { use, other, count, of } = pair;
  return `People use “${use.name}”${use.name === other.name ? ` #${use.id}` : ''} for this brand (${count} of ${of})`;
};

export const SITUATION_TIPS = {
  same_product_default: 'People pick another product about half the time here. Compare the words that differ: model, material, size.',
  other_colour: 'Another colour of the same style counts as the same product. People link it about 8 in 10 times.',
  beauty_shade: 'Beauty is one product per formula, not per shade. Nail polish lines are the exception.',
  pick_under_other_brand: link => `Our pick is filed under “${link.pick.brand}”. If the page's brand is right, add it under that brand instead.`,
  preowned_listing: 'On one-of-a-kind shops each listing is its own product. People linked these to the new item 1 in 4 times.',
  several_close_matches: "It's one of these about 4 in 10 times. If none fits, search: another 2 in 10 are already in the catalog.",
  new_product_default: "New products from the last month often don't show up in our matches. Search if it looks familiar.",
  brand_not_in_catalog: "Fewer than 1 in 4 keep the page's spelling. Look for the brand under a shorter or plainer name first.",
  new_product_not_sure: 'People add only about half of these; 3 in 10 are already in the catalog. Search first (C).',
  same_name_exists: 'A product with this or a very close name exists. People linked to it about 3 in 10 times. Compare the photos.',
  no_photo: "We couldn't get a photo. Open the page (O) and add one when you create it.",
  what_is_it_default: 'Half of these are already in the catalog, often listed from another shop. Open it (O), then search.',
  email_link: 'This is an email link. Open it (O) to see the product it leads to.',
  search_results_link: 'Search-result links usually point at one product. Match the product they searched for.',
  amazon_link: "Amazon links lead to a real product. Find it or add it, please don't skip it.",
  vinted_link: "The catalog doesn't take Vinted listings. Skip it unless the catalog already has this exact listing.",
  sold_one_of_a_kind: 'Each listing here is one item. Sold listings are skipped 95 in 100 times.',
  out_of_stock_therealreal: "“Out of stock” on TheRealReal can come back. Only “Sold” means it's gone.",
  collection_page: 'Some shops name a collection after one product. If the page shows one item, add it instead (N).'
};

// A site matches its subdomains too, so gap.com covers bananarepublic.gap.com and oldnavy.gap.com.
export const SITE_TIPS = [
  { domains: ['aritzia.com'], questions: ['same_product'], tip: 'Aritzia keeps one product per style. Another colour is still a match (9 in 10).' },
  {
    domains: ['mytheresa.com', 'neimanmarcus.com', 'bergdorfgoodman.com'],
    questions: ['same_product'],
    tip: 'Our first pick is often wrong on this site: people choose another product 6 in 10 times.'
  },
  {
    domains: ['zara.com', 'hm.com', 'mango.com', 'massimodutti.com'],
    questions: ['same_product', 'new_product'],
    tip: link => `${link.page?.brand || 'This shop'} reuses names for different items. Compare the photos, not the names.`
  },
  {
    domains: ['michaelkors.com'],
    questions: ['same_product'],
    tip: '“MICHAEL Michael Kors” and “Michael Kors” are the same brand here. People linked all 13 like this.'
  },
  { domains: ['farfetch.com'], tip: "People treat Farfetch links as new items (95%). Link to the brand's product." },
  { domains: ['on.com'], tip: 'On sells new shoes. Treat these as new items.' },
  { domains: ['fashionphile.com'], tip: 'No single habit here: people add, link and skip about equally. Ask for help (H) if unsure.' },
  {
    domains: ['thredup.com'],
    questions: ['new_product'],
    tip: 'ThredUp listings are single second-hand items and sell within weeks. Add each as its own pre-owned product.'
  },
  {
    domains: ['therealreal.com', '1stdibs.com', 'chairish.com', 'vestiairecollective.com'],
    questions: ['skip'],
    tip: 'Each listing here is one item. Sold listings are skipped 95 in 100 times.'
  },
  {
    domains: ['gap.com', 'gapfactory.com'],
    questions: ['what_is_it'],
    tip: 'Gap stores keep one product per style. If the style is in the catalog in another colour, link that one.'
  },
  {
    domains: ['hm.com'],
    questions: ['what_is_it'],
    tip: 'Usually already in the catalog (6 in 10). This store often keeps a product per colour, so check the colour.'
  },
  {
    domains: ['cos.com', 'arket.com', 'stories.com'],
    questions: ['what_is_it'],
    tip: 'Usually already in the catalog (8 in 10). This store often keeps a product per colour, so check the colour.'
  },
  {
    domains: ['net-a-porter.com', 'revolve.com', 'ysl.com'],
    questions: ['what_is_it'],
    tip: 'Usually already in the catalog (8 in 10). Search before adding.'
  },
  {
    domains: ['iherb.com', 'marshalls.com', 'wolfandbadger.com'],
    questions: ['what_is_it'],
    tip: 'Most of these are new to the catalog (7 in 10 added).'
  }
];

// Brands with two records where at least 4 answers picked one record 85% of the time or more.
export const BRAND_TIPS = [
  { use: { id: 79520, name: 'Pull&Bear' }, other: { id: 16412, name: 'PULL and BEAR' }, count: 19, of: 20 },
  { use: { id: 33767, name: 'Christopher Esber' }, other: { id: 991, name: 'Christopher Esber' }, count: 12, of: 13 },
  { use: { id: 10128, name: 'Torrid' }, other: { id: 18472, name: 'Torrid.com' }, count: 11, of: 13 },
  { use: { id: 6231, name: 'boohoo' }, other: { id: 11191, name: 'boohoo.com' }, count: 10, of: 10 },
  { use: { id: 47204, name: 'Camilla and Marc' }, other: { id: 8987, name: 'Camilla & Marc' }, count: 7, of: 7 },
  { use: { id: 24855, name: 'Stitchy Fish  (www.stitchyfish.com)' }, other: { id: 17810, name: 'Stitchy Fish' }, count: 7, of: 7 },
  { use: { id: 51767, name: 'BERLOOK' }, other: { id: 10998, name: 'BERLOOK US' }, count: 6, of: 7 },
  { use: { id: 83632, name: 'Maaji' }, other: { id: 14849, name: 'Maaji WW' }, count: 6, of: 6 },
  { use: { id: 28186, name: 'Hey Harper' }, other: { id: 13478, name: 'Velvet Unicorn' }, count: 5, of: 5 },
  { use: { id: 18191, name: 'Halara' }, other: { id: 47529, name: 'FULL SCALE FULL SPEED PTE. LTD.' }, count: 5, of: 5 },
  { use: { id: 6063, name: "Trader Joe's" }, other: { id: 18493, name: "Trader Joe's" }, count: 5, of: 5 },
  { use: { id: 438, name: 'Alo' }, other: { id: 40080, name: 'Alo Yoga' }, count: 4, of: 4 },
  { use: { id: 65698, name: 'O.P.I' }, other: { id: 3881, name: 'OPI' }, count: 4, of: 4 }
];

const TAB_DEFAULT_TIPS = { same_product: 'same_product_default', new_product: 'new_product_default', what_is_it: 'what_is_it_default' };

const BEAUTY_CATEGORY = /\b(?:beauty|makeup|lip\w*|nail\w*|foundation|concealer|blush|bronzer|highlighter|eyeshadow|eyeliner|mascara|brow\w*|serum|cleanser|fragrance|perfume)\b/i;

const getActionChangingTip = link => {
  const shape = getLinkShape(link.url);
  const listing = getListingState(link);
  if (link.question === 'new_product' && link.proposal?.brandInCatalog === false) return SITUATION_TIPS.brand_not_in_catalog;
  if (link.question === 'new_product' && (link.holdReason === 'creation_image_needs_review' || (link.proposal && !link.proposal.image))) {
    return SITUATION_TIPS.no_photo;
  }
  if (shape === 'email') return SITUATION_TIPS.email_link;
  if (shape === 'amazon') return SITUATION_TIPS.amazon_link;
  if (listing === 'sold') return SITUATION_TIPS.sold_one_of_a_kind;
  if (listing === 'out_of_stock' && link.domain === 'therealreal.com') return SITUATION_TIPS.out_of_stock_therealreal;
  if (/^vinted\.[a-z]{2,3}(?:\.[a-z]{2})?$/.test(link.domain)) return SITUATION_TIPS.vinted_link;
  if (shape === 'collection') return SITUATION_TIPS.collection_page;
  if (shape === 'search') return SITUATION_TIPS.search_results_link;
  if (link.question === 'same_product' && link.page?.isOneOfAKindShop && link.pick && !link.pick.isUsed) return SITUATION_TIPS.preowned_listing;
  return null;
};

const getSiteTip = link => {
  const domain = link.domain || '';
  return SITE_TIPS.find(
    ({ domains, questions }) =>
      domains.some(site => domain === site || domain.endsWith(`.${site}`)) && (!questions || questions.includes(link.question))
  )?.tip;
};

const getSituationTip = link => {
  if (link.question === 'same_product' && link.pick) {
    const differences = _.keyBy(getLinkDifferences({ page: link.page, product: link.pick }), 'key');
    if (link.page?.variant && differences.name?.status === 'same' && !differences.variant && differences.brand?.status !== 'different') {
      return BEAUTY_CATEGORY.test(link.pick.category?.name || '') ? SITUATION_TIPS.beauty_shade : SITUATION_TIPS.other_colour;
    }
    if (differences.brand?.status === 'different') return SITUATION_TIPS.pick_under_other_brand;
  }
  if (link.question === 'same_product' && !link.pick && link.candidates?.length > 1) return SITUATION_TIPS.several_close_matches;
  if (
    link.question === 'new_product' &&
    (link.similarInCatalog?.length || ['duplicate_product', 'duplicate_product_unresolved', 'creation_possible_duplicate'].includes(link.holdReason))
  ) {
    return SITUATION_TIPS.same_name_exists;
  }
  if (link.question === 'new_product' && link.holdReason === 'below_threshold') return SITUATION_TIPS.new_product_not_sure;
  return null;
};
